/**
 * Phase 131B — StreamX Stream History
 * Academy owners see their past broadcasts; super_admin sees all.
 * Reads GET /api/livex/streams (already RBAC-scoped server-side).
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import '@/styles/livex.css';
import { useAuth } from '@/contexts/AuthContext';
import {
  Radio, ArrowLeft, Eye, TrendingUp, Calendar, Trophy, ExternalLink,
  AlertTriangle, Plus, Clock, Sparkles,
} from 'lucide-react';
import LivexEmptyState from '@/components/livex/LivexEmptyState';
import { LivexShimmerRow } from '@/components/livex/LivexShimmer';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function StreamRow({ s }) {
  const live = s.status === 'live';
  return (
    <div
      className="p-4 sm:p-5 bg-[#07111F] border-2 border-[#22314F] rounded-xl hover:border-[#FF5A1F] transition-colors"
      data-testid={`livex-history-row-${s.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {live ? (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-rose-600 text-white text-[10px] font-black uppercase tracking-wider rounded">
                <span className="h-1.5 w-1.5 rounded-full bg-white livex-live-dot" /> LIVE
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-700 text-white text-[10px] font-black uppercase tracking-wider rounded">ENDED</span>
            )}
            {s.sport && <span className="text-xs font-bold text-white capitalize">{s.sport}</span>}
            {s.academy_name && <span className="text-xs font-bold text-[#94A3B8]">· {s.academy_name}</span>}
          </div>
          <h3 className="text-lg font-black text-white truncate">{s.title}</h3>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to={`/livex/watch/${s.id}`}
            target="_blank"
            data-testid={`livex-history-watch-${s.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-[#07111F] hover:bg-[#0c1a2e] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-lg transition-all"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Watch
          </Link>
          {live && (
            <Link
              to={`/livex/studio/${s.id}`}
              data-testid={`livex-history-studio-${s.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white border-2 border-[#FF5A1F] rounded-lg transition-all hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}
            >
              <Radio className="h-3.5 w-3.5" /> Re-enter
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4 text-[#94A3B8]" />
          <div>
            <div className="livex-detail-key">Now</div>
            <div className="livex-detail-value tabular-nums">{s.viewer_count_estimate || 0}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-[#FF5A1F]" />
          <div>
            <div className="livex-detail-key">Peak</div>
            <div className="livex-detail-value tabular-nums">{s.peak_viewers || 0}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-[#94A3B8]" />
          <div>
            <div className="livex-detail-key">Started</div>
            <div className="livex-detail-value" style={{ fontSize: '12px' }}>{formatDate(s.created_at)}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-[#94A3B8]" />
          <div>
            <div className="livex-detail-key">Ended</div>
            <div className="livex-detail-value" style={{ fontSize: '12px' }}>{s.ended_at ? formatDate(s.ended_at) : (live ? 'Still live' : '—')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LivexHistory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [livekitConfigured, setLivekitConfigured] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get('/livex/streams?limit=100');
        if (!active) return;
        setStreams(data.entries || []);
        setLivekitConfigured(!!data.livekit_configured);
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.detail || 'Failed to load streams');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const liveCount = streams.filter((s) => s.status === 'live').length;
  const totalPeak = streams.reduce((acc, s) => Math.max(acc, s.peak_viewers || 0), 0);
  const totalStreams = streams.length;

  return (
    <div className="min-h-screen text-white" style={{ background: '#0a0a0f' }} data-testid="livex-history">
      <div className="border-b-2 border-[#22314F] bg-[#07111F]/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate(-1)} data-testid="livex-history-back" className="flex-shrink-0 hover:opacity-80 transition-opacity">
              <ArrowLeft className="h-5 w-5 text-white" />
            </button>
            <div className="h-10 w-10 rounded-xl grid place-items-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
              <Trophy className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-xs tracking-[0.2em] font-bold text-[#FF5A1F]">STREAMX · HISTORY</div>
              <h1 className="text-xl sm:text-2xl font-black text-white">Stream Library</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {user?.academy_slug && (
              <Link
                to={`/livex/academy/${user.academy_slug}/top-moments`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="livex-history-brand-page"
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-white bg-[#07111F] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-lg transition-all"
              >
                <Sparkles className="h-4 w-4 text-[#FF5A1F]" /> Brand Page
              </Link>
            )}
            <Link
              to="/streamx/studio"
              data-testid="livex-history-new"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-white border-2 border-[#FF5A1F] rounded-lg transition-all hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}
            >
              <Plus className="h-4 w-4" /> New Stream
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* KPI strip */}
        <div className="grid grid-cols-3 gap-3">
          <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
            <div className="livex-detail-key mb-1">Live now</div>
            <div className="text-2xl sm:text-3xl font-black text-white tabular-nums" data-testid="livex-history-stat-live">{liveCount}</div>
          </div>
          <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
            <div className="livex-detail-key mb-1">All streams</div>
            <div className="text-2xl sm:text-3xl font-black text-white tabular-nums" data-testid="livex-history-stat-total">{totalStreams}</div>
          </div>
          <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
            <div className="livex-detail-key mb-1 flex items-center gap-1"><TrendingUp className="h-3 w-3 text-[#FF5A1F]" /> Best peak</div>
            <div className="text-2xl sm:text-3xl font-black text-white tabular-nums" data-testid="livex-history-stat-peak">{totalPeak}</div>
          </div>
        </div>

        {!livekitConfigured && (
          <div className="p-4 bg-amber-950/40 border-2 border-amber-700 rounded-xl flex items-start gap-3" data-testid="livex-history-mock-banner">
            <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-bold text-amber-200">LiveKit not configured</div>
              <div className="livex-body-text" style={{ fontSize: '13px' }}>Streams are in preview mode. Add LIVEKIT_* env vars to enable real broadcasting.</div>
            </div>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="space-y-3" data-testid="livex-history-loading">
            {Array.from({ length: 4 }).map((_, i) => <LivexShimmerRow key={i} />)}
          </div>
        ) : error ? (
          <div className="p-5 bg-rose-950 border-2 border-rose-700 rounded-xl text-rose-100 text-sm font-bold flex items-start gap-2" data-testid="livex-history-error">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" /> {error}
          </div>
        ) : streams.length === 0 ? (
          <LivexEmptyState
            testid="livex-history-empty"
            icon={Radio}
            title="No streams yet"
            body="Your stream library will fill up as you broadcast. Start your first match — it takes 60 seconds."
            ctaLabel="Start your first broadcast"
            ctaTo="/livex/studio"
            ctaIcon={Radio}
            secondaryLabel="See pricing"
            secondaryTo="/livex/pricing"
          />
        ) : (
          <div className="space-y-3">
            {streams.map((s) => <StreamRow key={s.id} s={s} />)}
          </div>
        )}
      </div>
    </div>
  );
}
